// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminPersonas (migrado al shell · prompt 0471)
// CRUD table para personas, usa shared/components/admin/
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchPersonas, type PersonaResumen } from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { AdminPersonaModal } from './AdminPersonaModal';
import { AdminPersonaPanel } from './AdminPersonaPanel';
import {
  AdminPageHeader,
  AdminTableShell,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

// ─── Badge config ─────────────────────────────────────────────

const TIPO_BADGE: Record<string, { bg: string; text: string }> = {
  EMPLEADO: { bg: '#EBF5FF', text: '#1F3864' },
  COLABORADOR: { bg: '#FFF7ED', text: '#C2410C' },
  EXTERNO: { bg: '#F3F4F6', text: '#6B7280' },
};

const ESTADO_BADGE: Record<string, { bg: string; text: string }> = {
  ACTIVO: { bg: '#ECFDF5', text: '#16A34A' },
  BAJA: { bg: '#FEF2F2', text: '#DC2626' },
  SUSPENDIDO: { bg: '#FFF7ED', text: '#F59E0B' },
};

function Badge({
  value,
  palette,
}: {
  value: string;
  palette: Record<string, { bg: string; text: string }>;
}) {
  const c = palette[value] ?? { bg: '#F3F4F6', text: '#6B7280' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.text,
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────────

export function AdminPersonas() {
  const { t } = useI18n();
  const adm = t.structure.admin;
  const pag = t.common.pagination;

  const [personas, setPersonas] = useState<PersonaResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalPersona, setModalPersona] = useState<PersonaResumen | null | 'new'>(null);
  const [toast, setToast] = useState('');
  const [panelPersonaId, setPanelPersonaId] = useState<string | null>(null);

  // 0608: si llegamos con ?personaId=X (deep-link desde modal Configuracion ->
  // Usuarios -> "Editar en Estructura"), abrir el panel automaticamente y
  // limpiar el query param para que no quede pegado en la URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pid = searchParams.get('personaId');
    if (pid) {
      setPanelPersonaId(pid);
      const next = new URLSearchParams(searchParams);
      next.delete('personaId');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce buscar
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch paginado server-side
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params: Record<string, string> = { pagina: String(page), porPagina: '100' };
        if (debouncedSearch) params.q = debouncedSearch;
        const res = await fetchPersonas(params);
        if (cancelled) return;
        setPersonas(((res as Record<string, unknown>).datos as typeof res.data) ?? res.data ?? []);
        const meta = res.meta ?? res;
        setTotal(meta.total ?? 0);
      } catch {
        if (!cancelled) setPersonas([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, refreshKey]);

  const loadPersonas = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const columns: AdminTableColumn<PersonaResumen>[] = [
    {
      key: 'codigo',
      label: adm.colCodigo,
      mono: true,
      sortable: true,
      sortKey: (r) => r.codigo,
    },
    {
      key: 'nombre',
      label: adm.colNombre,
      sortable: true,
      sortKey: (r) => `${r.nombre ?? ''} ${r.apellidos ?? ''}`.trim(),
      render: (r) => (
        <span>
          {r.nombre} {r.apellidos}
        </span>
      ),
    },
    {
      key: 'email',
      label: adm.colEmail,
      sortable: true,
      render: (r) => <span style={{ color: '#64748B' }}>{r.email || '—'}</span>,
    },
    {
      key: 'tipoPersona',
      label: adm.colTipo,
      render: (r) => <Badge value={r.tipoPersona} palette={TIPO_BADGE} />,
    },
    {
      key: 'estado',
      label: adm.colEstado,
      render: (r) => <Badge value={r.estado} palette={ESTADO_BADGE} />,
    },
    {
      key: 'puesto',
      label: adm.colPuesto,
      render: (r) => <span style={{ fontSize: 12 }}>{r.puestoActual?.puesto?.titulo ?? '—'}</span>,
    },
    {
      key: 'departamento',
      label: adm.colDepartamento,
      render: (r) => (
        <span style={{ fontSize: 12 }}>{r.puestoActual?.puesto?.departamento?.nombre ?? '—'}</span>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={adm.personasTitle}
        infoTooltip={{
          title: t.structure.tooltips.adminPersonas.titulo,
          text: t.structure.tooltips.adminPersonas.ayuda,
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search,
        }}
        onNew={{
          label: adm.newPersona,
          onClick: () => setModalPersona('new'),
        }}
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={personas}
        rowKey={(p) => p.id}
        onRowClick={(p) => setPanelPersonaId(p.id)}
        loading={loading}
        loadingMessage={t.common.cargando ?? 'Cargando…'}
        emptyMessage={adm.noResults}
        pagination={
          total > 100
            ? {
                page,
                pageSize: 100,
                total,
                onPageChange: setPage,
                labels: {
                  prev: pag.anterior,
                  next: pag.siguiente,
                  pageOf: pag.pageOf,
                  totalCount: (t) => `${t} ${adm.countLabel}`,
                },
              }
            : undefined
        }
        footer={
          !loading && total > 0 && total <= 100 ? (
            <span>
              {Math.min((page - 1) * 100 + 1, total)}–{Math.min(page * 100, total)} de {total}{' '}
              {adm.countLabel}
            </span>
          ) : undefined
        }
      />

      {/* Modal creación "Nueva persona" — edit existente vive en el panel */}
      {modalPersona !== null && (
        <AdminPersonaModal
          persona={modalPersona === 'new' ? null : modalPersona}
          onClose={() => setModalPersona(null)}
          onSaved={() => {
            setModalPersona(null);
            loadPersonas();
            showToast(adm.saved);
          }}
        />
      )}

      {/* Panel lateral completo + baja inline */}
      <AdminPersonaPanel
        personaId={panelPersonaId}
        onClose={() => setPanelPersonaId(null)}
        onRefresh={loadPersonas}
      />
    </div>
  );
}
